import React, { useState, useEffect } from 'react';
import subscriptionService from '../services/subscriptionService';
import {
  Crown, Star, Check, X,
  Building2, ClipboardList, FolderOpen,
  MessageCircle, BarChart3, Video, Cpu,
  Phone, RefreshCw, AlertCircle, Headphones
} from 'lucide-react';
import './SubscriptionPage.css';

// Reads plan.features.* and returns display rows
const FEATURE_ROWS = [
  { key: 'offersLimit',       label: 'عدد العروض',       icon: Building2,   type: 'limit' },
  { key: 'requestsLimit',     label: 'عدد الطلبات',      icon: ClipboardList, type: 'limit' },
  { key: 'catalogsLimit',     label: 'عدد الكتالوجات',   icon: FolderOpen,  type: 'limit' },
  { key: 'chatEnabled',       label: 'المحادثات',         icon: MessageCircle, type: 'bool' },
  { key: 'reportsEnabled',    label: 'التقارير',          icon: BarChart3,   type: 'bool' },
  { key: 'videoUploadEnabled',label: 'رفع الفيديو',       icon: Video,       type: 'bool' },
  { key: 'aiAnalysisEnabled', label: 'تحليل AI',          icon: Cpu,         type: 'bool' },
  { key: 'supportType',       label: 'الدعم الفني',       icon: Headphones,  type: 'support' },
];

const SUPPORT_LABELS = {
  basic:     'أساسي',
  email:     'عبر البريد',
  priority:  'ذو أولوية',
  dedicated: 'مخصص',
};

const formatLimit = (val) => {
  if (val === -1 || val === null || val === undefined) return '∞';
  if (val === 0) return '٠';
  return val;
};

const isLimitOk   = (val) => val !== 0;
const isBoolOk    = (val) => val === true;
const isSupportOk = (val) => !!val && val !== 'basic';

const featureOk = (row, features) => {
  const v = features?.[row.key];
  if (row.type === 'limit')   return isLimitOk(v);
  if (row.type === 'bool')    return isBoolOk(v);
  if (row.type === 'support') return isSupportOk(v);
  return false;
};

const featureDisplay = (row, features) => {
  const v = features?.[row.key];
  if (row.type === 'limit')   return formatLimit(v);
  if (row.type === 'bool')    return v ? 'مفعّل' : 'غير مفعّل';
  if (row.type === 'support') return SUPPORT_LABELS[v] || v || '—';
  return '—';
};

const daysLeft = (endDate) => {
  if (!endDate) return null;
  const diff = new Date(endDate) - new Date();
  return Math.max(0, Math.ceil(diff / 86400000));
};

// ─── Plan colour / icon helpers ────────────────────────────────────────────
const ICON_MAP = { Crown, Star };
const planColor = (p) => p.color || '#6b7280';
const planIcon  = (p) => ICON_MAP[p.icon] || Crown;

export default function SubscriptionPage() {
  const [plans, setPlans]         = useState([]);
  const [mySub, setMySub]         = useState(null);   // active UserSubscription
  const [usage, setUsage]         = useState(null);   // { offers, requests, catalogs }
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [subscribing, setSubscribing] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // getPlans returns { plans, currentSubscription }
        // getUsage  returns { subscription, usage }
        const [plansRes, usageRes] = await Promise.all([
          subscriptionService.getPlans(),
          subscriptionService.getUsage(),
        ]);

        if (plansRes?.success) {
          setPlans(plansRes.data?.plans || []);
        }

        if (usageRes?.success) {
          setMySub(usageRes.data?.subscription || null);
          setUsage(usageRes.data?.usage || null);
        }
      } catch {
        setError('تعذّر تحميل بيانات الاشتراك. تأكد من اتصالك وحاول مجدداً.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSubscribe = async (plan) => {
    if (subscribing) return;
    const ok = window.confirm(`هل تريد الاشتراك في خطة "${plan.nameAr}"؟`);
    if (!ok) return;
    setSubscribing(plan._id);
    try {
      const res = await subscriptionService.subscribe(plan._id);
      if (res?.success) {
        window.location.reload();
      } else {
        alert(res?.message || 'فشل الاشتراك. حاول مجدداً.');
      }
    } catch {
      alert('حدث خطأ أثناء الاشتراك.');
    } finally {
      setSubscribing(null);
    }
  };

  // ── Derived ──────────────────────────────────────────────────────────────
  const currentPlan    = mySub?.plan || null;
  const currentPlanId  = currentPlan?._id;
  const remaining      = mySub?.endDate ? daysLeft(mySub.endDate) : null;
  const isFreePlan     = !mySub || mySub.subscriptionType === 'free';

  // ── Loading / Error ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="sub-page">
        <div className="sub-loading">
          <RefreshCw size={32} className="sub-spin" />
          <p>جاري تحميل بيانات الاشتراك...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sub-page">
        <div className="sub-error">
          <AlertCircle size={32} />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const PlanIcon = currentPlan ? planIcon(currentPlan) : Crown;
  const color    = currentPlan ? planColor(currentPlan) : '#6b7280';
  const features = currentPlan?.features || {};

  return (
    <div className="sub-page" dir="rtl">

      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div className="sub-header">
        <Crown size={28} />
        <h1>خطتي والترقية</h1>
      </div>

      {/* ── Current plan card ────────────────────────────────────────────── */}
      <div className="sub-current-card" style={{ borderColor: color }}>
        <div className="sub-current-top" style={{ background: color }}>
          <PlanIcon size={28} className="sub-current-icon" />
          <div>
            <div className="sub-current-name">
              {currentPlan?.nameAr || 'الخطة المجانية'}
            </div>
            <div className="sub-current-label">خطتك الحالية</div>
          </div>
          {remaining !== null && (
            <div className="sub-current-days">
              <span className="sub-days-num">{remaining}</span>
              <span className="sub-days-txt">يوم متبقٍّ</span>
            </div>
          )}
          {isFreePlan && (
            <div className="sub-current-days">
              <span className="sub-days-txt">مجاني</span>
            </div>
          )}
        </div>

        <div className="sub-current-body">

          {/* Usage bars */}
          {usage && (
            <div className="sub-usage-section">
              <h3>الاستخدام الحالي</h3>
              <div className="sub-usage-grid">
                <UsageBar label="العروض"      used={usage.offers}   limit={features.offersLimit}   icon={Building2}    color={color} />
                <UsageBar label="الطلبات"     used={usage.requests} limit={features.requestsLimit} icon={ClipboardList} color={color} />
                <UsageBar label="الكتالوجات" used={usage.catalogs} limit={features.catalogsLimit} icon={FolderOpen}   color={color} />
              </div>
            </div>
          )}

          {/* Features list */}
          <div className="sub-features-section">
            <h3>مميزات خطتك</h3>
            <div className="sub-features-list">
              {FEATURE_ROWS.map((row) => {
                const ok  = featureOk(row, features);
                const val = featureDisplay(row, features);
                const Icon = row.icon;
                return (
                  <div key={row.key} className={`sub-feature-item ${ok ? 'ok' : 'no'}`}>
                    <Icon size={15} />
                    <span className="sub-feature-label">{row.label}</span>
                    <span className="sub-feature-val">{val}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── Plans comparison ─────────────────────────────────────────────── */}
      {plans.length > 0 && (
        <div className="sub-plans-section">
          <h2>جميع الخطط المتاحة</h2>
          <div className="sub-plans-grid">
            {plans.map((plan) => {
              const isCurrent   = plan._id === currentPlanId;
              // Enterprise = price missing OR plan.icon is Crown + no price
              const isEnterprise = plan.price === undefined || plan.price === null || plan.price < 0;
              const c    = planColor(plan);
              const Icon = planIcon(plan);
              const pf   = plan.features || {};

              return (
                <div
                  key={plan._id}
                  className={`sub-plan-card ${isCurrent ? 'current' : ''}`}
                  style={{ '--plan-color': c }}
                >
                  {isCurrent && <div className="sub-plan-badge">خطتك الحالية</div>}

                  {/* Card header */}
                  <div className="sub-plan-top" style={{ background: c }}>
                    <Icon size={22} />
                    <div className="sub-plan-name">{plan.nameAr}</div>
                    <div className="sub-plan-price">
                      {isEnterprise
                        ? 'تواصل معنا'
                        : plan.price === 0
                          ? 'مجاني'
                          : `${plan.price} ر.س / شهر`
                      }
                    </div>
                  </div>

                  {/* Feature rows */}
                  <div className="sub-plan-body">
                    {FEATURE_ROWS.map((row) => {
                      const ok  = featureOk(row, pf);
                      const val = featureDisplay(row, pf);
                      const RowIcon = row.icon;
                      return (
                        <div key={row.key} className={`sub-plan-feature ${ok ? 'ok' : 'no'}`}>
                          {ok
                            ? <Check size={14} className="feat-check" />
                            : <X    size={14} className="feat-x"     />
                          }
                          <RowIcon size={13} />
                          <span>{row.label}</span>
                          <span className="feat-val">
                            {row.type === 'bool' ? '' : val}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {/* CTA */}
                  <div className="sub-plan-footer">
                    {isCurrent ? (
                      <button className="sub-btn-current" disabled>خطتك الحالية</button>
                    ) : isEnterprise ? (
                      <a
                        href="https://wa.me/966500000000"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sub-btn-contact"
                      >
                        <Phone size={15} />
                        تواصل معنا
                      </a>
                    ) : (
                      <button
                        className="sub-btn-subscribe"
                        style={{ background: c }}
                        onClick={() => handleSubscribe(plan)}
                        disabled={!!subscribing}
                      >
                        {subscribing === plan._id
                          ? <><RefreshCw size={14} className="sub-spin" /> جاري...</>
                          : 'اختر هذه الخطة'
                        }
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Fallback */}
      {plans.length === 0 && (
        <div className="sub-empty">
          <Crown size={48} />
          <p>لا توجد خطط متاحة حالياً.</p>
        </div>
      )}
    </div>
  );
}

// ─── Usage bar ────────────────────────────────────────────────────────────
function UsageBar({ label, used = 0, limit, icon: Icon, color }) {
  const unlimited = limit === -1 || limit === null || limit === undefined;
  const pct       = unlimited ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const isHigh    = !unlimited && pct >= 80;

  return (
    <div className="usage-bar-wrap">
      <div className="usage-bar-header">
        <div className="usage-bar-label">
          <Icon size={14} />
          <span>{label}</span>
        </div>
        <span className={`usage-bar-count ${isHigh ? 'high' : ''}`}>
          {used} / {unlimited ? '∞' : limit}
        </span>
      </div>
      {!unlimited ? (
        <div className="usage-bar-track">
          <div
            className={`usage-bar-fill ${isHigh ? 'high' : ''}`}
            style={{ width: `${pct}%`, background: isHigh ? '#ef4444' : color }}
          />
        </div>
      ) : (
        <div className="usage-bar-unlimited">غير محدود</div>
      )}
    </div>
  );
}
